import { useParams, Link } from 'react-router-dom';
import SEOHead from '@/components/feature/SEOHead';
import Navbar from '@/components/feature/Navbar';
import Footer from '@/components/feature/Footer';
import Breadcrumb from '@/components/feature/Breadcrumb';
import ArticleCard from '@/components/feature/ArticleCard';
import { articles } from '@/mocks/articles';

export default function ArticlePage() {
  const { id } = useParams<{ id: string }>();
  const article = articles.find((a) => a.id === id);

  if (!article) {
    return (
      <div className="min-h-screen bg-background-50">
        <Navbar />
        <div className="max-w-7xl mx-auto px-4 md:px-6 pt-24 pb-20 text-center">
          <div className="w-20 h-20 mx-auto rounded-full bg-background-100 flex items-center justify-center mb-6">
            <i className="ri-emotion-sad-line text-3xl text-foreground-400"></i>
          </div>
          <h1 className="text-2xl font-bold text-foreground-950 mb-3">文章未找到</h1>
          <p className="text-foreground-600 mb-6">您访问的文章不存在或已被删除</p>
          <Link
            to="/"
            className="inline-flex items-center gap-2 px-6 py-2.5 bg-primary-500 text-background-50 rounded-full font-medium text-sm hover:bg-primary-600 transition-colors whitespace-nowrap"
          >
            <i className="ri-arrow-left-line"></i>
            返回首页
          </Link>
        </div>
        <Footer />
      </div>
    );
  }

  const categoryName =
    article.category === 'mingxing-bagua' ? '明星八卦' :
    article.category === 'zongyi-jieMu' ? '综艺节目' :
    article.category === 'yingshi-zixun' ? '影视资讯' :
    article.category === 'wanghong-redian' ? '网红热点' : '社会热点';

  const relatedArticles = articles
    .filter((a) => a.category === article.category && a.id !== article.id)
    .slice(0, 3);

  const moreArticles = articles
    .filter((a) => a.category !== article.category)
    .slice(0, 3);

  const formattedDate = new Date(article.publishedAt).toLocaleDateString('zh-CN', {
    year: 'numeric',
    month: 'long',
    day: 'numeric',
    hour: '2-digit',
    minute: '2-digit',
  });

  const articleSchema = {
    '@context': 'https://schema.org',
    '@type': 'Article',
    headline: article.title,
    description: article.excerpt,
    image: article.imageUrl,
    datePublished: article.publishedAt,
    author: {
      '@type': 'Person',
      name: article.author,
    },
    publisher: {
      '@type': 'Organization',
      name: '17吃瓜',
    },
    mainEntityOfPage: {
      '@type': 'WebPage',
      '@id': `https://17chigua.com/article/${article.id}`,
    },
  };

  return (
    <>
      <SEOHead
        title={`${article.title} - 17吃瓜`}
        description={article.excerpt}
        keywords={`${article.tags.join(',')},17吃瓜,吃瓜热点,娱乐爆料`}
        canonicalUrl={`https://17chigua.com/article/${article.id}`}
        ogType="article"
        ogImage={article.imageUrl}
        schema={articleSchema}
      />

      <div className="min-h-screen bg-background-50">
        <Navbar />

        <main className="pt-20 pb-16">
          <div className="max-w-4xl mx-auto px-4 md:px-6">
            <Breadcrumb
              items={[
                { label: '首页', href: '/' },
                { label: categoryName, href: `/category/${article.category}` },
                { label: article.title },
              ]}
            />

            <article>
              {/* Article Header */}
              <header className="mb-8">
                <div className="flex items-center gap-3 mb-4 flex-wrap">
                  <Link
                    to={`/category/${article.category}`}
                    className="text-xs font-medium px-3 py-1.5 rounded-full bg-primary-100 text-primary-600 hover:bg-primary-200 transition-colors whitespace-nowrap"
                  >
                    {categoryName}
                  </Link>
                  {article.isHot && (
                    <span className="text-xs font-medium px-3 py-1.5 rounded-full bg-secondary-100 text-secondary-600 flex items-center gap-1 whitespace-nowrap">
                      <i className="ri-fire-line text-xs"></i>
                      热门
                    </span>
                  )}
                </div>
                <h1 className="text-3xl md:text-4xl font-bold text-foreground-950 leading-tight">
                  {article.title}
                </h1>
                <div className="flex flex-wrap items-center gap-5 mt-5 text-sm text-foreground-500">
                  <span className="flex items-center gap-1.5">
                    <i className="ri-user-line text-foreground-400"></i>
                    {article.author}
                  </span>
                  <span className="flex items-center gap-1.5">
                    <i className="ri-time-line text-foreground-400"></i>
                    {formattedDate}
                  </span>
                  <span className="flex items-center gap-1.5">
                    <i className="ri-eye-line text-foreground-400"></i>
                    {article.views > 10000 ? `${(article.views / 10000).toFixed(1)}万阅读` : `${article.views}阅读`}
                  </span>
                  <span className="flex items-center gap-1.5">
                    <i className="ri-chat-3-line text-foreground-400"></i>
                    {article.comments}评论
                  </span>
                </div>
              </header>

              {/* Featured Image */}
              <figure className="mb-10 rounded-xl overflow-hidden">
                <img
                  src={article.imageUrl}
                  alt={article.title}
                  className="w-full h-auto object-cover"
                  title={`${article.title} - 17吃瓜`}
                />
              </figure>

              {/* Article Content */}
              <div
                className="prose prose-lg max-w-none text-foreground-800 leading-relaxed"
                dangerouslySetInnerHTML={{ __html: article.content }}
              />

              {/* Tags */}
              <div className="mt-10 flex flex-wrap items-center gap-2">
                <span className="text-sm text-foreground-500 font-medium">标签：</span>
                {article.tags.map((tag) => (
                  <span
                    key={tag}
                    className="px-3 py-1.5 text-xs font-medium rounded-full bg-background-100 text-foreground-600 border border-background-200/70 whitespace-nowrap"
                  >
                    #{tag}
                  </span>
                ))}
              </div>

              {/* Share */}
              <div className="mt-8 pt-8 border-t border-background-200/70 flex flex-wrap items-center gap-4">
                <span className="text-sm text-foreground-600 font-medium">分享到：</span>
                <button className="w-10 h-10 rounded-full bg-background-100 flex items-center justify-center hover:bg-primary-100 hover:text-primary-500 transition-colors text-foreground-600 cursor-pointer">
                  <i className="ri-weibo-line text-lg"></i>
                </button>
                <button className="w-10 h-10 rounded-full bg-background-100 flex items-center justify-center hover:bg-primary-100 hover:text-primary-500 transition-colors text-foreground-600 cursor-pointer">
                  <i className="ri-wechat-line text-lg"></i>
                </button>
                <button className="w-10 h-10 rounded-full bg-background-100 flex items-center justify-center hover:bg-primary-100 hover:text-primary-500 transition-colors text-foreground-600 cursor-pointer">
                  <i className="ri-share-forward-line text-lg"></i>
                </button>
              </div>
            </article>

            {/* Related Articles */}
            <section className="mt-14">
              <h2 className="text-xl font-bold text-foreground-950 mb-6 flex items-center gap-2">
                <i className="ri-links-line text-primary-500"></i>
                相关爆料
              </h2>
              <div className="grid grid-cols-1 sm:grid-cols-3 gap-5">
                {relatedArticles.map((a) => (
                  <ArticleCard key={a.id} article={a} />
                ))}
              </div>
            </section>

            {/* More Articles */}
            <section className="mt-12">
              <h2 className="text-xl font-bold text-foreground-950 mb-6 flex items-center gap-2">
                <i className="ri-flashlight-line text-primary-500"></i>
                推荐阅读
              </h2>
              <div className="grid grid-cols-1 sm:grid-cols-3 gap-5">
                {moreArticles.map((a) => (
                  <ArticleCard key={a.id} article={a} />
                ))}
              </div>
            </section>
          </div>
        </main>

        <Footer />
      </div>
    </>
  );
}